import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TerminalSquare } from 'lucide-react';
import { cn } from 'lib/utils';

const COLORS = {
  bg: '#0d1117',
  text: '#c9d1d9',
  green: '#3fb950',
  blue: '#58a6ff',
  yellow: '#e3b341',
  purple: '#bc8cff',
  muted: '#8b949e',
} as const;

const BANNER_ART = String.raw`
 ███████╗ █████╗ ██╗  ██╗ █████╗ ██████╗ ██╗ █████╗
 ╚══███╔╝██╔══██╗██║ ██╔╝██╔══██╗██╔══██╗██║██╔══██╗
   ███╔╝ ███████║█████╔╝ ███████║██████╔╝██║███████║
  ███╔╝  ██╔══██║██╔═██╗ ██╔══██║██╔══██╗██║██╔══██║
 ███████╗██║  ██║██║  ██╗██║  ██║██║  ██║██║██║  ██║
 ╚══════╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝╚═╝  ╚═╝
`;

interface HistoryEntry {
  id: number;
  command: string;
  output: React.ReactNode;
}

const PROMPT_LABEL = (
  <span className="select-none">
    <span style={{ color: COLORS.green }}>❯</span>{' '}
    <span style={{ color: COLORS.blue }}>~</span>{' '}
    <span style={{ color: COLORS.purple }}>(main)</span>
  </span>
);

const HelpOutput: React.FC = () => (
  <div>
    <div style={{ color: COLORS.muted }}>Available commands:</div>
    <div className="mt-1 grid grid-cols-[8rem_1fr] gap-x-2">
      <span style={{ color: COLORS.green }}>help</span>
      <span>list all commands</span>
      <span style={{ color: COLORS.green }}>whoami</span>
      <span>print short bio</span>
      <span style={{ color: COLORS.green }}>skills</span>
      <span>technical skills by category</span>
      <span style={{ color: COLORS.green }}>experience</span>
      <span>professional experience</span>
      <span style={{ color: COLORS.green }}>projects</span>
      <span>featured projects with stack</span>
      <span style={{ color: COLORS.green }}>education</span>
      <span>academic background</span>
      <span style={{ color: COLORS.green }}>contact</span>
      <span>contact details</span>
      <span style={{ color: COLORS.green }}>banner</span>
      <span>show the welcome banner</span>
      <span style={{ color: COLORS.green }}>clear</span>
      <span>clear the terminal</span>
    </div>
  </div>
);

const WhoamiOutput: React.FC = () => (
  <div>
    <span style={{ color: COLORS.blue }}>Dani Zakaria</span>
    <span> – Full Stack Java Developer (Spring Boot & React), Marrakech 🇲🇦</span>
  </div>
);

interface SkillGroup {
  title: string;
  items: string[];
}

const SKILL_GROUPS: SkillGroup[] = [
  {
    title: 'Backend',
    items: [
      'Java 8/17/21',
      'Spring Boot',
      'Spring Cloud',
      'Kafka',
      'RabbitMQ',
      'Redis',
      'Oracle',
      'PostgreSQL',
      'MySQL',
    ],
  },
  {
    title: 'Frontend',
    items: ['React', 'TypeScript', 'Redux Toolkit', 'Material UI'],
  },
  {
    title: 'Security',
    items: ['Keycloak', 'OAuth2', 'JWT', 'Vault', 'Consul'],
  },
  {
    title: 'Tools',
    items: ['Docker', 'Maven', 'Jenkins', 'Git', 'JUnit', 'Mockito', 'Flyway', 'Jira'],
  },
];

const SkillsOutput: React.FC = () => (
  <div className="space-y-2">
    {SKILL_GROUPS.map((group) => (
      <div key={group.title}>
        <div style={{ color: COLORS.yellow }}>{group.title}</div>
        <div className="ml-2">
          {group.items.map((item, idx) => (
            <span key={item}>
              <span style={{ color: COLORS.green }}>•</span>{' '}
              <span>{item}</span>
              {idx < group.items.length - 1 ? <span style={{ color: COLORS.muted }}>, </span> : null}
            </span>
          ))}
        </div>
      </div>
    ))}
  </div>
);

interface Position {
  company: string;
  role: string;
  period: string;
  projects: string[];
}

const POSITIONS: Position[] = [
  {
    company: 'Adria Business & Technology',
    role: 'Software Dev Engineer',
    period: 'July 2023 – Present',
    projects: ['Financing Management Platform', 'E-Payment Platform'],
  },
  {
    company: 'Adria Business & Technology',
    role: 'Full Stack Intern (PFE)',
    period: 'Feb 2023 – July 2023',
    projects: ['Trade Finance & Bank Guarantees System'],
  },
  {
    company: 'Norsys Afrique',
    role: 'Full Stack Intern (PFA)',
    period: 'July – Sep 2022',
    projects: ['Event Management Application'],
  },
];

const ExperienceOutput: React.FC = () => (
  <div className="space-y-3">
    {POSITIONS.map((position, idx) => (
      <div key={`${position.company}-${position.period}`}>
        <div>
          <span style={{ color: COLORS.yellow }}>{idx + 1}.</span>{' '}
          <span style={{ color: COLORS.blue }}>{position.company}</span>
          <span style={{ color: COLORS.muted }}> — </span>
          <span>{position.role}</span>
        </div>
        <div className="ml-4" style={{ color: COLORS.muted }}>
          {position.period}
        </div>
        <div className="ml-4">
          <span style={{ color: COLORS.purple }}>Projects:</span>{' '}
          {position.projects.join(', ')}
        </div>
      </div>
    ))}
  </div>
);

interface Project {
  name: string;
  company: string;
  stack: string[];
}

const PROJECTS: Project[] = [
  {
    name: 'Financing Management Platform',
    company: 'Adria Business & Technology',
    stack: ['Java 17', 'Spring Boot', 'Spring Cloud', 'Kafka', 'PostgreSQL', 'React', 'TypeScript', 'Keycloak'],
  },
  {
    name: 'E-Payment Platform',
    company: 'Adria Business & Technology',
    stack: ['Java 21', 'Spring Boot', 'RabbitMQ', 'Redis', 'Oracle', 'React', 'OAuth2', 'JWT'],
  },
  {
    name: 'Trade Finance & Bank Guarantees System',
    company: 'Adria Business & Technology',
    stack: ['Java 8', 'Spring Boot', 'MySQL', 'React', 'Redux Toolkit', 'Material UI'],
  },
  {
    name: 'Event Management Application',
    company: 'Norsys Afrique',
    stack: ['Java', 'Spring Boot', 'React', 'MySQL'],
  },
];

const ProjectsOutput: React.FC = () => (
  <div className="space-y-2">
    {PROJECTS.map((project) => (
      <div key={project.name}>
        <div>
          <span style={{ color: COLORS.green }}>▸</span>{' '}
          <span style={{ color: COLORS.blue }}>{project.name}</span>
        </div>
        <div className="ml-4" style={{ color: COLORS.muted }}>
          @ {project.company}
        </div>
        <div className="ml-4">
          <span style={{ color: COLORS.purple }}>stack:</span> {project.stack.join(' · ')}
        </div>
      </div>
    ))}
  </div>
);

const EducationOutput: React.FC = () => (
  <div>
    <div>
      <span style={{ color: COLORS.blue }}>ENSA Marrakech</span>
      <span style={{ color: COLORS.muted }}> — </span>
      <span>Software Engineer</span>
    </div>
    <div className="ml-2" style={{ color: COLORS.muted }}>
      2018 – 2023
    </div>
  </div>
);

const ContactOutput: React.FC = () => (
  <div className="space-y-1">
    <div>
      <span style={{ color: COLORS.yellow }}>phone </span>
      <span style={{ color: COLORS.muted }}>→ </span>
      <span>+212-632-461-833</span>
    </div>
    <div>
      <span style={{ color: COLORS.yellow }}>email </span>
      <span style={{ color: COLORS.muted }}>→ </span>
      <a
        href="mailto:zakariadani25@gmail.com"
        className="underline-offset-2 hover:underline"
        style={{ color: COLORS.blue }}
      >
        zakariadani25@gmail.com
      </a>
    </div>
    <div>
      <span style={{ color: COLORS.yellow }}>location </span>
      <span style={{ color: COLORS.muted }}>→ </span>
      <span>Marrakech, Maroc</span>
    </div>
  </div>
);

const BannerOutput: React.FC = () => (
  <pre
    className="leading-tight text-[11px] sm:text-sm whitespace-pre overflow-x-auto"
    style={{ color: COLORS.green }}
  >
    {BANNER_ART}
  </pre>
);

const UnknownOutput: React.FC<{ command: string }> = ({ command }) => (
  <div>
    <span>command not found: </span>
    <span style={{ color: COLORS.yellow }}>{command}</span>
    <span style={{ color: COLORS.muted }}>  (type </span>
    <span style={{ color: COLORS.green }}>help</span>
    <span style={{ color: COLORS.muted }}> for available commands)</span>
  </div>
);

const renderOutput = (raw: string): React.ReactNode => {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const [command] = trimmed.split(/\s+/);
  switch (command) {
    case 'help':
      return <HelpOutput />;
    case 'whoami':
      return <WhoamiOutput />;
    case 'skills':
      return <SkillsOutput />;
    case 'experience':
      return <ExperienceOutput />;
    case 'projects':
      return <ProjectsOutput />;
    case 'education':
      return <EducationOutput />;
    case 'contact':
      return <ContactOutput />;
    case 'banner':
      return <BannerOutput />;
    default:
      return <UnknownOutput command={command} />;
  }
};

const WELCOME_TEXT = 'Welcome! Type help to see available commands.';

const HackerMode: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [commandLog, setCommandLog] = useState<string[]>([]);
  const [cursor, setCursor] = useState<number>(-1);
  const [input, setInput] = useState('');
  const nextIdRef = useRef(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasBootedRef = useRef(false);

  const appendHistory = useCallback((command: string, output: React.ReactNode) => {
    setHistory((prev) => [
      ...prev,
      { id: nextIdRef.current++, command, output },
    ]);
  }, []);

  const runCommand = useCallback(
    (raw: string) => {
      const trimmed = raw.trim();
      if (trimmed === '') {
        appendHistory('', null);
        return;
      }
      if (trimmed === 'clear') {
        setHistory([]);
        return;
      }
      appendHistory(trimmed, renderOutput(trimmed));
    },
    [appendHistory]
  );

  useEffect(() => {
    if (!isOpen) {
      hasBootedRef.current = false;
      setIsMinimized(false);
      setIsMaximized(false);
      return;
    }
    if (hasBootedRef.current) return;
    hasBootedRef.current = true;
    setHistory([]);
    appendHistory('banner', <BannerOutput />);
    appendHistory('', <span style={{ color: COLORS.muted }}>{WELCOME_TEXT}</span>);
  }, [isOpen, appendHistory]);

  useEffect(() => {
    if (!isOpen) return;
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [history, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const t = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.removeProperty('overflow');
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isMaximized) { setIsMaximized(false); return; }
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isMaximized]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = input;
      runCommand(value);
      if (value.trim().length > 0) {
        setCommandLog((prev) => [...prev, value]);
      }
      setCursor(-1);
      setInput('');
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (commandLog.length === 0) return;
      const nextIndex = cursor === -1 ? commandLog.length - 1 : Math.max(0, cursor - 1);
      setCursor(nextIndex);
      setInput(commandLog[nextIndex] ?? '');
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (commandLog.length === 0 || cursor === -1) {
        setCursor(-1);
        setInput('');
        return;
      }
      const nextIndex = cursor + 1;
      if (nextIndex >= commandLog.length) {
        setCursor(-1);
        setInput('');
      } else {
        setCursor(nextIndex);
        setInput(commandLog[nextIndex] ?? '');
      }
    }
  };

  const monoStyle = useMemo<React.CSSProperties>(
    () => ({ fontFamily: '"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace' }),
    []
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-pressed={isOpen}
        aria-label={isOpen ? 'Exit hacker mode' : 'Enter hacker mode'}
        className={cn(
          'fixed bottom-8 right-8 z-[110] inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-lg transition-all duration-300',
          'backdrop-blur supports-[backdrop-filter]:bg-opacity-80',
          isOpen
            ? 'border-emerald-400 bg-[#0d1117] text-emerald-300 hover:bg-[#161b22]'
            : 'border-emerald-500/40 bg-background text-foreground hover:border-emerald-500 hover:text-emerald-500'
        )}
        style={monoStyle}
      >
        <TerminalSquare className="h-4 w-4" />
        <span>{isOpen ? '< Exit Hacker Mode >' : '< Hacker Mode >'}</span>
      </button>

      <div
        className={cn(
          'fixed inset-0 z-[100] flex items-end sm:items-center justify-center transition-all duration-500',
          isMaximized ? 'p-0' : 'px-3 py-6 sm:px-8 sm:py-10',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        style={{ backgroundColor: isMinimized ? 'rgba(0,0,0,0)' : 'rgba(0, 0, 0, 0.6)' }}
        aria-hidden={!isOpen}
      >
        <div
          className={cn(
            'flex flex-col overflow-hidden border shadow-2xl transition-all duration-300',
            isMaximized ? 'w-full h-full rounded-none' : 'w-full max-w-4xl rounded-lg',
            isOpen && !isMinimized ? 'translate-y-0 scale-100' : isMinimized ? '' : 'translate-y-4 scale-95',
          )}
          style={{
            backgroundColor: COLORS.bg,
            color: COLORS.text,
            borderColor: '#1f2933',
            height: isMaximized ? '100%' : isMinimized ? 'auto' : undefined,
            ...(isMaximized ? {} : isMinimized ? {} : { height: '80vh' }),
            ...monoStyle,
          }}
          onClick={() => !isMinimized && inputRef.current?.focus()}
        >
          {/* Title bar */}
          <div
            className="flex items-center px-4 py-2 border-b flex-shrink-0"
            style={{ borderColor: '#1f2933', backgroundColor: '#161b22' }}
          >
            <div className="group flex items-center gap-2">
              {/* Red – close */}
              <button
                type="button"
                aria-label="Close terminal"
                onClick={() => setIsOpen(false)}
                className="relative h-3 w-3 rounded-full bg-[#ff5f56] hover:brightness-90 transition-all"
              >
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 text-[8px] leading-none font-bold text-black/60 select-none">
                  ✕
                </span>
              </button>

              {/* Yellow – minimize / restore */}
              <button
                type="button"
                aria-label={isMinimized ? 'Restore terminal' : 'Minimize terminal'}
                onClick={() => setIsMinimized((v) => !v)}
                className="relative h-3 w-3 rounded-full bg-[#ffbd2e] hover:brightness-90 transition-all"
              >
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 text-[8px] leading-none font-bold text-black/60 select-none">
                  {isMinimized ? '+' : '−'}
                </span>
              </button>

              {/* Green – maximize / restore */}
              <button
                type="button"
                aria-label={isMaximized ? 'Restore terminal size' : 'Maximize terminal'}
                onClick={() => { setIsMaximized((v) => !v); setIsMinimized(false); }}
                className="relative h-3 w-3 rounded-full bg-[#27c93f] hover:brightness-90 transition-all"
              >
                <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 text-[8px] leading-none font-bold text-black/60 select-none">
                  {isMaximized ? '⊖' : '⊕'}
                </span>
              </button>
            </div>

            <div
              className="flex-1 text-center text-xs sm:text-sm"
              style={{ color: COLORS.muted }}
            >
              zakaria@portfolio:~
              {isMinimized && <span style={{ color: COLORS.green }}> [minimized]</span>}
            </div>
            <div className="w-14" aria-hidden />
          </div>

          <div
            ref={scrollRef}
            className={cn(
              'overflow-y-auto px-4 py-3 text-xs sm:text-sm transition-all duration-300',
              isMinimized ? 'h-0 py-0 overflow-hidden' : 'flex-1'
            )}
            style={monoStyle}
          >
            {history.map((entry) => (
              <div key={entry.id} className="mb-2">
                {entry.command !== '' && (
                  <div className="flex flex-wrap gap-x-2">
                    {PROMPT_LABEL}
                    <span>{entry.command}</span>
                  </div>
                )}
                {entry.output ? <div className="mt-1">{entry.output}</div> : null}
              </div>
            ))}

            <div className="flex items-center gap-2">
              {PROMPT_LABEL}
              <input
                ref={inputRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                autoCapitalize="off"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                aria-label="Terminal input"
                className="flex-1 bg-transparent outline-none border-0 caret-emerald-400"
                style={{ color: COLORS.text, ...monoStyle }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HackerMode;
